<template>
  <div class="subject-management-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة المواد الدراسية</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة مادة جديدة
          </el-button>
        </div>
      </template>

      <el-alert
        title="ما هي هذه الواجهة؟"
        type="info"
        description="هذه الواجهة تسمح لك بتعريف جميع المواد الدراسية التي تقدمها المدرسة (مثال: الرياضيات، اللغة العربية). يجب إضافة المواد هنا أولاً قبل أن تتمكن من ربطها بالمعلمين أو الفصول الدراسية."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <el-table :data="subjects" v-loading="loading" border style="width: 100%">
        <el-table-column prop="name" label="اسم المادة" />
        <el-table-column prop="code" label="الرمز" width="120" />
        <el-table-column prop="category.name" label="التصنيف" />
        <el-table-column prop="description" label="الوصف" />
        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm
              title="هل أنت متأكد من حذف هذه المادة؟"
              confirm-button-text="نعم"
              cancel-button-text="لا"
              @confirm="handleDelete(row.id)"
            >
              <template #reference><el-button size="small" type="danger">حذف</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Dialog لإنشاء/تعديل المادة -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="50%" :close-on-click-modal="false">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="اسم المادة" prop="name">
          <el-input v-model="form.name" placeholder="مثال: الرياضيات" />
        </el-form-item>
        <el-form-item label="رمز المادة" prop="code">
          <el-input v-model="form.code" placeholder="مثال: MATH101" />
        </el-form-item>
        <el-form-item label="تصنيف المادة" prop="subject_category_id">
          <el-select v-model="form.subject_category_id" placeholder="اختر التصنيف" style="width: 100%;">
            <el-option v-for="category in categories" :key="category.id" :label="category.name" :value="category.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="الوصف" prop="description">
          <el-input v-model="form.description" type="textarea" placeholder="وصف موجز للمادة" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">
            {{ form.id ? 'تحديث' : 'إنشاء' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';

export default {
  name: 'SubjectManagement',
  components: { Plus, ElPopconfirm },
  data() {
    return {
      api: null,
      loading: true,
      subjects: [],
      categories: [],
      dialogVisible: false,
      formLoading: false,
      form: this.getInitialForm(),
      rules: {
        name: [{ required: true, message: 'اسم المادة مطلوب', trigger: 'blur' }],
        code: [{ required: true, message: 'رمز المادة مطلوب', trigger: 'blur' }],
        subject_category_id: [{ required: false, message: 'تصنيف المادة مطلوب', trigger: 'change' }],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل مادة دراسية' : 'إضافة مادة دراسية جديدة';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchInitialData();
  },
  methods: {
    getInitialForm() {
      return { id: null, name: '', code: '', description: '', subject_category_id: null };
    },
    async fetchInitialData() {
      this.loading = true;
      try {
        const [subjectsRes, categoriesRes] = await Promise.all([
          this.api.get('/subjects'),
          this.api.get('/subject-categories'), 
        ]);
        this.subjects = subjectsRes.data.data || subjectsRes.data;
        this.categories = categoriesRes.data.data || categoriesRes.data;
      } catch (error) {
        ElMessage.error('فشل في جلب البيانات.');
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.dialogVisible = true;
    },
    handleEdit(subject) {
      this.form = { ...subject };
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        try {
          if (this.form.id) {
            await this.api.put(`/subjects/${this.form.id}`, this.form);
            ElMessage.success('تم تحديث المادة بنجاح!');
          } else {
            await this.api.post('/subjects', this.form);
            ElMessage.success('تم إنشاء المادة بنجاح!');
          }
          this.dialogVisible = false;
          this.fetchInitialData();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/subjects/${id}`);
        ElMessage.success('تم حذف المادة بنجاح!');
        this.fetchInitialData();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشل في حذف المادة.');
      }
    },
  },
};
</script>

<style scoped>
.subject-management-container {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
